import { useEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { X as XIcon } from "lucide-react";
import "./Dialog.css";

const FOCUSABLE =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export default function Dialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
  initialFocusRef,
  ariaLabel,
}) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const titleId = useId();

  // Esc to close + focus trap
  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current =
      typeof document !== "undefined" ? document.activeElement : null;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onOpenChange && onOpenChange(false);
        return;
      }
      if (e.key === "Tab") {
        const node = dialogRef.current;
        if (!node) return;
        const focusables = Array.from(
          node.querySelectorAll(FOCUSABLE)
        ).filter((el) => !el.hasAttribute("disabled"));
        if (focusables.length === 0) {
          e.preventDefault();
          node.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  // Initial focus + restore on close + body scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const node = dialogRef.current;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Defer to next tick so the portal content is mounted.
    const focusTimer = setTimeout(() => {
      if (initialFocusRef && initialFocusRef.current) {
        try {
          initialFocusRef.current.focus();
          return;
        } catch (_) {}
      }
      if (node) {
        const focusables = Array.from(node.querySelectorAll(FOCUSABLE));
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          node.focus();
        }
      }
    }, 0);

    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = previousBodyOverflow;
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === "function") {
        try {
          prev.focus();
        } catch (_) {}
      }
    };
  }, [open, initialFocusRef]);

  if (!open || typeof document === "undefined") return null;

  const onBackdropMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      onOpenChange && onOpenChange(false);
    }
  };

  return createPortal(
    <div
      className="vs-dialog__backdrop"
      onMouseDown={onBackdropMouseDown}
    >
      <div
        ref={dialogRef}
        className={["vs-dialog", className].filter(Boolean).join(" ")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        tabIndex={-1}
      >
        {title && (
          <header className="vs-dialog__header">
            <h2 id={titleId} className="vs-dialog__title">
              {title}
            </h2>
            <button
              type="button"
              className="vs-dialog__close"
              aria-label="Close dialog"
              onClick={() => onOpenChange && onOpenChange(false)}
            >
              <XIcon size={16} />
            </button>
          </header>
        )}
        <div className="vs-dialog__body">{children}</div>
        {footer && <footer className="vs-dialog__footer">{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}
